/**
 * BudgetOverviewCard
 *
 * Dashboard widget summarising budget consumption for the current month.
 * Improvements in [T38P0]:
 * • Proper empty state with a "Create Budget" CTA.
 * • Colour-coded progress bars: green (≤75%), amber (76–99%), red (≥100%).
 * • Warning badge on over-budget categories.
 * • Remaining / over-budget amount shown below each bar.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, PiggyBank, AlertTriangle, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Budget } from "@/lib/types";
import { useMemo } from "react";
import { useCurrency } from "@/hooks/useCurrency";

interface BudgetOverviewCardProps {
  budgets: Budget[];
}

/** Map a spending percentage to a TailwindCSS colour class for the progress bar. */
function progressColor(pct: number): string {
  if (pct >= 100) return "[&>div]:bg-red-600";
  if (pct >= 75) return "[&>div]:bg-amber-500";
  return "[&>div]:bg-green-500";
}

export default function BudgetOverviewCard({ budgets }: BudgetOverviewCardProps) {
  const { fmt } = useCurrency();
  const budgetData = useMemo(() => {
    return budgets.flatMap((budget) =>
      budget.categories.map((cat, index) => ({
        id: `${budget.id}-${cat.categoryId || index}`,
        categoryName: cat.categoryName,
        budgeted: cat.budgeted,
        spent: cat.spent,
        // Cap display value at 100 so the bar doesn't overflow, but keep raw pct
        percentage: cat.budgeted > 0 ? (cat.spent / cat.budgeted) * 100 : 0,
        isOverBudget: cat.spent > cat.budgeted,
        isWarning: cat.budgeted > 0 && cat.spent / cat.budgeted >= 0.75 && cat.spent <= cat.budgeted,
        remaining: cat.budgeted - cat.spent,
      }))
    );
  }, [budgets]);

  const totalBudget = budgets.reduce(
    (sum, b) => sum + b.categories.reduce((s, c) => s + c.budgeted, 0),
    0
  );
  const totalSpent = budgets.reduce(
    (sum, b) => sum + b.categories.reduce((s, c) => s + c.spent, 0),
    0
  );
  // [T28P0] Guard against division-by-zero when no budgets exist
  const totalPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
  const totalRemaining = totalBudget - totalSpent;

  const hasNoBudgets = budgets.length === 0;
  const overBudgetCount = budgetData.filter((b) => b.isOverBudget).length;
  // [T-DASHBOARD-HEADERLESS] Carve-out: BudgetOverviewCard is a standalone card used on
  // the home / insights pages (LegacyDashboard, Dashboard, HomeMirrorLayout) — NOT inside
  // the WidgetFrame system. The new opt-in glass-panel semantics (Task 9) live in the
  // WidgetFrame system; standalone cards like this one are intentionally exempt and keep
  // the static `glass-panel` class so the visual effect persists regardless of any opt-in
  // prop on the parent.
  return (
    <Card
      data-section="budget-overview"
      data-testid="home-card-progress-budget"
      className="transition-all duration-200 hover:shadow-md hover:border-primary/20 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-full">
              <PiggyBank className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Budget Overview</CardTitle>
              {overBudgetCount > 0 && (
                <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {overBudgetCount} {overBudgetCount === 1 ? "category" : "categories"} over budget
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-primary"
            asChild
          >
            <Link to="/budgets" className="flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </>
      </CardHeader>
      <CardContent>
      {hasNoBudgets ? (
          /* ---------------------------------------------------------------- */
          /* Empty state                                                       */
          /* ---------------------------------------------------------------- */
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/60 mb-3">
              <PiggyBank className="h-7 w-7 text-muted-foreground" />
            </div>
            <h4 className="text-sm font-semibold text-foreground mb-1">
              No budgets yet
            </h4>
            <p className="text-xs text-muted-foreground mb-4 max-w-[190px]">
              Set monthly spending limits to stay on track.
            </p>
            <Button size="sm" asChild>
              <Link to="/budgets" className="flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Create Budget
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Total summary */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Total Budget</span>
                <span
                  className={
                    totalSpent > totalBudget
                      ? "text-red-600 dark:text-red-400 font-medium"
                      : "text-muted-foreground"
                  }
                >
                  {fmt(totalSpent)} /{" "}
                  {fmt(totalBudget)}
                </span>
              </div>
              <Progress
                value={Math.min(totalPercentage, 100)}
                className={`h-2.5 ${progressColor(totalPercentage)}`}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {totalPercentage.toFixed(1)}% used
                </p>
                {totalBudget > 0 && (
                  <p
                    className={`text-xs font-medium ${
                      totalRemaining < 0
                        ? "text-red-500"
                        : "text-muted-foreground"
                    }`}
                  >
                    {totalRemaining < 0
                      ? `${fmt(Math.abs(totalRemaining))} over`
                      : `${fmt(totalRemaining)} left`}
                  </p>
                )}
              </div>
            </div>

            {/* Per-category rows */}
            {budgetData.length > 0 && (
              <div className="space-y-3.5 pt-3 border-t">
                {budgetData.slice(0, 4).map((budget) => (
                  <div key={budget.id} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-medium truncate">
                          {budget.categoryName}
                        </span>
                        {budget.isOverBudget && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1 h-4 py-0 leading-none text-red-600 border-red-300 shrink-0"
                          >
                            Over
                          </Badge>
                        )}
                        {budget.isWarning && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1 h-4 py-0 leading-none text-amber-600 border-amber-300 shrink-0"
                          >
                            Near limit
                          </Badge>
                        )}
                      </div>
                      <span
                        className={`shrink-0 ml-2 ${
                          budget.isOverBudget
                            ? "text-red-600 dark:text-red-400 font-medium"
                            : "text-muted-foreground"
                        }`}
                      >
                        {fmt(budget.spent)} / {fmt(budget.budgeted)}
                      </span>
                    </div>
                    <Progress
                      value={Math.min(budget.percentage, 100)}
                      className={`h-1.5 ${progressColor(budget.percentage)}`}
                    />
                    <p
                      className={`text-[10px] ${
                        budget.remaining < 0
                          ? "text-red-500"
                          : "text-muted-foreground"
                      }`}
                    >
                      {budget.remaining < 0
                        ? `${fmt(Math.abs(budget.remaining))} over budget`
                        : `${fmt(budget.remaining)} remaining`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
